import { rtkQueryApiServices, rtkQueryServiceTags } from "../../services/api";

const { PROGRAM, GOALS, DEPLOYMENT } = rtkQueryServiceTags;

// Helper function to build query string
const buildQueryString = (query) => {
  let queryParams = "";

  if (query.value === "planned") {
    query = { ...query, value: "yettojoin" };
  }

  if (query && Object.keys(query).length) {
    if (query.type) {
      queryParams =
        (queryParams === "" ? "?" : "&") + `${query.type}=${query.value}`;
    }
    if (query.page) {
      queryParams =
        (queryParams === "" ? "?" : `${queryParams}&`) +
        `${query.page}=${query.number}`;
    }
    if (query.search) {
      queryParams =
        (queryParams === "" ? "?" : `${queryParams}&`) +
        `${query.search.search}=${query.search.value}`;
    }
    if (query.date) {
      queryParams =
        (queryParams === "" ? "?" : `${queryParams}&`) +
        `${query.date.date}=${query.date.value}`;
    }
    if (query.category_id) {
      queryParams =
        (queryParams === "" ? "?" : `${queryParams}&`) +
        `category_id=${query.category_id}`;
    }
  }

  return queryParams !== "" ? `${queryParams}&limit=6` : "?limit=6";
};

export const programsApi = rtkQueryApiServices.injectEndpoints({
  endpoints: (builder) => ({
    // Create new program
    createProgram: builder.mutation({
      query: (bodyFormData) => ({
        url: "programs",
        method: "POST",
        body: bodyFormData,
      }),
    }),

    // Update program
    updateProgram: builder.mutation({
      query: ({ program_id, bodyFormData, method = "PUT" }) => ({
        url: `programs/${program_id}`,
        method: method,
        body: bodyFormData,
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : [PROGRAM, DEPLOYMENT],
    }),

    updateProgramReopen: builder.mutation({
      query: ({ program_id, bodyFormData, role }) => ({
        url: `programs`,
        method: "POST",
        body: bodyFormData,
      }),
      invalidatesTags: [PROGRAM],
    }),

    // Update program
    updateProgramStatus: builder.mutation({
      query: (body) => ({
        url: `update_program`,
        method: "POST",
        body,
      }),
      invalidatesTags: [PROGRAM],
    }),

    updateAdminProgramStatus: builder.mutation({
      query: (body) => ({
        url: `request/`,
        method: "POST",
        body,
      }),
      invalidatesTags: [PROGRAM],
    }),

    getCancellationReasonCodes: builder.query({
      query: () => ({
        url: "request/cancellation-reason-codes",
      }),
    }),

    // Get all categories
    getAllCategories: builder.query({
      query: (params) => ({ url: "category", params }),
    }),

    //get Program Metrics

    getProgramMetrics: builder.query({
      query: (params) => ({ url: `program-metrics`, params }),
    }),

    getProgramDeliveryMetrics: builder.query({
      query: (params) => ({ url: `program-metrics`, params }),
    }),

    getProgramCategoriesMetrics: builder.query({
      query: (params) => ({ url: `program-metrics`, params }),
    }),

    // Get Program Goals
    getProgramGoals: builder.query({
      query: (params) => ({ url: `goals/program-goals`, params }),
      providesTags: [GOALS],
    }),

    // Get skills by category
    getSkills: builder.query({
      query: (params) => ({ url: `skills`, params }),
    }),

    // Get members by category
    getMembers: builder.query({
      query: (params) => ({ url: `members`, params }),
    }),

    // Get all mentors
    getAllMentors: builder.query({
      query: (params) => ({
        url: "members/member-list",
        params,
      }),
    }),

    // Get programs by category
    getProgramsByCategory: builder.query({
      query: (categoryId) =>
        `program_task_assign/list_program?id=${categoryId}`,
    }),

    // Get program mentees
    getProgramMentees: builder.query({
      query: (programId) => `program/participates?program_id=${programId}`,
    }),

    // Validate program name
    validateProgramName: builder.mutation({
      query: (params) => ({
        url: `programs/validate_program_name`,
        method: "GET",
        params,
      }),
    }),

    // Get program list with category
    getProgramListWithCategory: builder.query({
      query: (categoryId) =>
        `/program/completed-program-list?category_id=${categoryId}&type=task`,
    }),
    // Programs
    getAllPrograms: builder.query({
      query: (params) => ({
        url: `programs`,
        params,
        responseHandler: (response) =>
          params.export ? response.blob() : response.json(),
      }),
      transformResponse: (response) => response,
      providesTags: [PROGRAM],
    }),

    /**
     * Fetch a single recurring child occurrence for timesheet.
     * Kept separate from getAllPrograms so list + occurrence caches do not collide.
     */
    getTimesheetOccurrenceProgram: builder.query({
      query: ({ parent_id, timesheet_occurrences, ...rest } = {}) => ({
        url: `programs`,
        params: {
          parent_id,
          timesheet_occurrences,
          limit: 1,
          page: 1,
          ...rest,
        },
      }),
      transformResponse: (response) => response?.programs?.[0] ?? null,
      providesTags: (_result, _error, arg) => [
        {
          type: PROGRAM,
          id: `timesheet-occurrence-${arg?.parent_id}-${arg?.timesheet_occurrences}`,
        },
      ],
    }),

    getCountryStates: builder.query({
      query: () => ({
        url: `program/states`,
      }),
    }),

    getCities: builder.query({
      query: (params) => ({
        url: `program/cities`,
        params,
      }),
    }),

    getAllAdminProgramDetails: builder.query({
      query: (params) => ({ url: "program/admin-program", params }),
      // transformResponse: (response) => response.program,
    }),

    // Get program details
    getProgramDetailsById: builder.query({
      query: ({ id, requestId, role }) => ({
        url:
          role === "admin"
            ? `program/admin-program/${id}`
            : `programs/${id}${requestId ? `?request_id=${requestId}` : ""}`,
      }),
      providesTags: [PROGRAM],
    }),

    getSpecificProgramDetails: builder.query({
      query: ({ id, requestId = "", program_create_type }) =>
        `programs/${id}${requestId ? `?request_id=${requestId}` : ""}${
          program_create_type
            ? `?program_create_type=${program_create_type}`
            : ""
        }`,
      providesTags: [PROGRAM],
    }),

    launchProgram: builder.mutation({
      query: (data) => ({
        url: "request/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [PROGRAM],
    }),

    acceptProgram: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `request/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [PROGRAM],
    }),

    joinAllProgram: builder.mutation({
      query: (id) => ({
        url: `request/join-all-subprograms/${id}`,
        method: "POST",
      }),
      invalidatesTags: [PROGRAM],
    }),

    // Program Counts and Statistics
    getProgramCounts: builder.query({
      query: (params) => ({
        url: `programs/program-status-count`,
        params,
      }),
    }),

    // Mentees
    getMentees: builder.query({
      query: (params = "") => ({
        url: "/program_task_assign/list_mentee",
        params,
      }),
    }),

    getProgramTaskMentees: builder.query({
      query: (id = "") => `program/participates?program_id=${id}`,
    }),

    getMenteePrograms: builder.query({
      query: (query) => ({
        url: `programs${buildQueryString(query)}`,
        transformResponse: (response) => ({
          ...response,
          filterType: query?.type || "",
          filterValue: query?.value || "",
        }),
      }),
    }),

    // Program Tasks
    assignProgramTask: builder.mutation({
      query: (data) => ({
        url: "program_task_assign/create_task",
        method: "POST",
        body: data,
        headers: { "Content-Type": "application/json" },
      }),
    }),

    updateProgramTask: builder.mutation({
      query: (data) => ({
        url: "program_task_assign/mentortask",
        method: "PATCH",
        body: data,
        headers: { "Content-Type": "application/json" },
      }),
    }),

    startProgramTask: builder.mutation({
      query: (data) => ({
        url: "program_task_assign/task_start",
        method: "PATCH",
        body: data,
      }),
    }),

    // Other endpoints
    getChartProgramList: builder.query({
      query: (filterBy) => `program-performance?filter_by=${filterBy}`,
    }),

    updateProgramImage: builder.mutation({
      query: (data) => ({
        url: "programs",
        method: "PATCH",
        body: data,
        headers: { "Content-Type": "multipart/form-data" },
      }),
    }),

    getMenteeJoinedInProgram: builder.mutation({
      query: (data) => ({
        url: "mentee_program/enroll_check",
        method: "POST",
        body: data,
      }),
    }),

    menteeJoinProgram: builder.mutation({
      query: (data) => ({
        url: "mentee_program/join_program",
        method: "POST",
        body: data,
      }),
    }),

    markProgramInterest: builder.mutation({
      query: (data) => ({
        url: "programs/interest",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [PROGRAM],
    }),

    postPrerequisites: builder.mutation({
      query: (data) => ({
        url: "programs/prerequisites",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [PROGRAM],
    }),

    updatePrerequisites: builder.mutation({
      query: (data) => ({
        url: "programs/update-prerequisites",
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [PROGRAM],
    }),

    verifyPrerequisites: builder.mutation({
      query: (data) => ({
        url: "programs/verify-prerequisites",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [PROGRAM],
    }),

    getMenteeSubmittedPrerequisites: builder.query({
      query: ({ program_id, member_id }) => ({
        url: `programs/prerequisites/${program_id}/${member_id}`,
        method: "GET",
      }),
      providesTags: [PROGRAM],
    }),

    getProgramParticipateList: builder.query({
      query: ({ program_id, status, role, assistance_type, page }) => {
        let queryParams = [];

        if (status) queryParams.push(`status=${status}`);
        if (role) queryParams.push(`role=${role}`);
        if (assistance_type) queryParams.push(`assistance_type=${assistance_type}`);
        if (page !== undefined) queryParams.push(`page=${page}`);
        const queryString = queryParams.length
          ? `?${queryParams.join("&")}`
          : "";
        return {
          url: `programs/${program_id}/participants${queryString}`,

          method: "GET",
        };
      },
      providesTags: [PROGRAM],
    }),

    checkIn: builder.mutation({
      query: (data) => ({
        url: "program/attendance/check-in/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [PROGRAM],
    }),

    getTimeSheetList: builder.query({
      query: (id, queryString) => ({
        url: `program/attendance/${id}/${queryString}`,
      }),
    }),

    checkOut: builder.mutation({
      query: (data) => ({
        url: "program/attendance/check-out/",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [PROGRAM],
    }),

    getProgramName: builder.query({
      query: (programName) =>
        `/program/completed-program-list?category_id=5&type=task&program_name=${programName}`,
    }),

    // Available equipment for program create/edit (POST with schedule + date range)
    getAvailableEquipments: builder.query({
      query: (body) => ({
        url: "equipment/available-equipments/",
        method: "POST",
        body,
      }),
      providesTags: [PROGRAM],
      transformResponse: (response) => response,
    }),

    getAvailableFieldStaff: builder.query({
      query: (body) => ({
        url: "field-staff/",
        method: "POST",
        body,
      }),
      providesTags: [PROGRAM],
      transformResponse: (response) => response,
    }),

    getFieldStaffbyId: builder.query({
      query: (id) => ({
        url: `field-staff/${id}/`,
      }),
      providesTags: (_result, _error, id) => [
        { type: PROGRAM, id: `field-staff-${id}` },
      ],
    }),

    editFieldStaff: builder.mutation({
      query: ({ id, body }) => ({
        url: `field-staff/${id}/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: PROGRAM, id: `field-staff-${id}` },
        PROGRAM,
      ],
    }),

    deleteFieldStaff: builder.mutation({
      query: ({ id, body }) => ({
        url: `field-staff/${id}/`,
        method: "DELETE",
        body,
      }),
    }),

    activateFieldStaff: builder.mutation({
      query: ({ id, body }) => ({
        url: `field-staff/${id}/`,
        method: "PATCH",
        body,
      }),
    }),

    // Get program types
    getProgramTypes: builder.query({
      query: () => "program/program-type/",
      transformResponse: (response) => response,
    }),

    // Get incident types
    getIncidentTypes: builder.query({
      query: () => "program/incident-type/",
      transformResponse: (response) => response,
    }),
    //update broadcast
    updateBroadCast: builder.mutation({
      query: (data) => ({
        url: "broadcast",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [PROGRAM],
    }),

    getLocationList: builder.query({
      query: (params = "") => ({
        url: `/locations`,
        params,
      }),
    }),

    previewScheduleOccurrences: builder.mutation({
      query: (body) => ({
        url: "programs/schedule-occurrences/preview",
        method: "POST",
        body,
      }),
    }),
    validateProgramSchedule: builder.mutation({
      query: ({ program_id, ...body }) => ({
        url: `programs/${program_id}/validate-schedule/`,
        method: "POST",
        body,
      }),
    }),
    getCloneEventList: builder.query({
      query: (params) => ({
        url: "programs/clone-event-list",
        params,
      }),
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const { page, ...otherArgs } = queryArgs;
        // Create a cache key that ignores the page parameter for infinite scroll
        return `${endpointName}(${JSON.stringify(otherArgs)})`;
      },
      merge: (currentCache, newItems, { arg }) => {
        const { page } = arg;
        if (page === 1) {
          // First page - replace cache
          return newItems;
        } else {
          // Subsequent pages - merge results
          return {
            ...newItems,
            results: [...(currentCache?.results || []), ...newItems.results],
          };
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        // Force refetch when page changes or other parameters change
        return currentArg !== previousArg;
      },
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetCloneEventListQuery,
  useUpdateBroadCastMutation,
  useGetAllAdminProgramDetailsQuery,
  useGetProgramDetailsByIdQuery,
  useGetSpecificProgramDetailsQuery,
  useUpdateProgramMutation,
  useUpdateProgramReopenMutation,
  useUpdateProgramStatusMutation,
  useLaunchProgramMutation,
  useJoinAllProgramMutation,
  useAcceptProgramMutation,
  useGetProgramCountsQuery,
  useGetProgramGoalsQuery,
  useGetMenteesQuery,
  useGetProgramTaskMenteesQuery,
  useGetMenteeProgramsQuery,
  useAssignProgramTaskMutation,
  useUpdateProgramTaskMutation,
  useStartProgramTaskMutation,
  useGetChartProgramListQuery,
  useUpdateProgramImageMutation,
  useGetMenteeJoinedInProgramMutation,
  useMenteeJoinProgramMutation,
  useGetAllProgramsQuery,
  useGetTimesheetOccurrenceProgramQuery,
  useCreateProgramMutation,
  useMarkProgramInterestMutation,
  usePostPrerequisitesMutation,
  useUpdatePrerequisitesMutation,
  useVerifyPrerequisitesMutation,
  useGetMenteeSubmittedPrerequisitesQuery,
  useGetProgramParticipateListQuery,
  useGetAllCategoriesQuery,
  useGetSkillsQuery,
  useGetMembersQuery,
  useGetAllMentorsQuery,
  useGetProgramsByCategoryQuery,
  useGetProgramMenteesQuery,
  useGetCountryStatesQuery,
  useGetCitiesQuery,
  useValidateProgramNameMutation,
  useGetProgramListWithCategoryQuery,
  useUpdateAdminProgramStatusMutation,
  useGetCancellationReasonCodesQuery,
  useGetProgramNameQuery,
  useGetProgramMetricsQuery,
  useGetProgramDeliveryMetricsQuery,
  useGetProgramCategoriesMetricsQuery,
  useCheckInMutation,
  useGetTimeSheetListQuery,
  useCheckOutMutation,
  useGetAvailableEquipmentsQuery,
  useGetAvailableFieldStaffQuery,
  useGetFieldStaffbyIdQuery,
  useEditFieldStaffMutation,
  useDeleteFieldStaffMutation,
  useActivateFieldStaffMutation,
  useGetProgramTypesQuery,
  useGetIncidentTypesQuery,
  useGetLocationListQuery,
  usePreviewScheduleOccurrencesMutation,
  useValidateProgramScheduleMutation,
} = programsApi;
